import React from 'react';
import { Avatar, Button, Card, Div, Group, Header, Panel, Progress, SimpleCell, Text, Title } from '@vkontakte/vkui';
import { Icon28BookOutline, Icon28ChevronRightOutline, Icon28LockOutline, Icon28SchoolOutline, Icon28StatisticsOutline, Icon28UserCircleOutline } from '@vkontakte/icons';
import { AppShell } from '../../components/Layout/AppShell';
import { responsiveGridClass } from '../../theme/classes';
import { categoryImagePath } from '../../utils/assets';
import { pluralize } from '../../utils/text';
import {
  getCategoryProgress,
  getCategoryRecommendation,
  getDifficultyMeta
} from '../../utils/categoryActivity';
import { CABINET_PANEL, DAILY_PANEL, HOME_PANEL } from '../../routing/routing';

function buildSortedCategories(categories, categoryActivity) {
  return categories
    .filter((item) => item.word_count > 0)
    .sort((left, right) => {
      const leftProgress = getCategoryProgress(categoryActivity, left);
      const rightProgress = getCategoryProgress(categoryActivity, right);

      if (leftProgress.hasStarted !== rightProgress.hasStarted) {
        return leftProgress.hasStarted ? -1 : 1;
      }
      if (leftProgress.percent !== rightProgress.percent) {
        return rightProgress.percent - leftProgress.percent;
      }
      if (left.word_count !== right.word_count) {
        return left.word_count - right.word_count;
      }
      return left.id - right.id;
    });
}

function CategoryIcon({ category, size = 44 }) {
  if (!category?.image) {
    return <Avatar size={size} gradientColor="blue"><Icon28SchoolOutline /></Avatar>;
  }

  return (
    <Avatar size={size} src={categoryImagePath(category.image)} />
  );
}

export function HomePanel({ nav = HOME_PANEL, player, categories, categoryActivity, navigateTo, openRating, openCategory }) {
  const sortedCategories = buildSortedCategories(categories, categoryActivity);
  const continueCategory = sortedCategories.find((item) => item.id === categoryActivity?.lastOpenedCategoryId) || null;
  const playerName = [player?.first_name, player?.last_name].filter(Boolean).join(' ') || 'Мой кабинет';

  return (
    <Panel nav={nav}>
      <AppShell>
        <Group>
          <SimpleCell
            before={player?.avatar_url ? <Avatar size={48} src={player.avatar_url} /> : <Avatar size={48}><Icon28UserCircleOutline /></Avatar>}
            after={<Icon28ChevronRightOutline />}
            subtitle="Прогресс, словарь и достижения"
            onClick={() => navigateTo(CABINET_PANEL)}
            expandable
          >
            {playerName}
          </SimpleCell>
          <Div>
            <Title level="1" className="mb-2">Словарные карточки</Title>
            <Text className="text-[var(--vkui--color_text_secondary)]">Ежедневная практика английских слов.</Text>
          </Div>
          <Div className={responsiveGridClass}>
            <Button size="l" stretched before={<Icon28BookOutline />} onClick={() => navigateTo(DAILY_PANEL)}>
              Слова на сегодня
            </Button>
            <Button size="l" stretched mode="secondary" before={<Icon28StatisticsOutline />} onClick={openRating}>
              Рейтинг учеников
            </Button>
          </Div>
        </Group>

        {continueCategory ? (
          <Group header={<Header>Продолжить</Header>}>
            <Div>
              <Card mode="outline">
                <Div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-start gap-3">
                    <CategoryIcon category={continueCategory} />
                    <div className="min-w-0 flex-1">
                      <Title level="3" className="break-words">{continueCategory.name}</Title>
                      <Text className="mt-1 text-[var(--vkui--color_text_secondary)]">
                        {getCategoryRecommendation(continueCategory, categoryActivity)}
                      </Text>
                    </div>
                  </div>
                  <Button size="m" stretched className="sm:w-auto sm:shrink-0" onClick={() => openCategory(continueCategory)}>
                    Открыть
                  </Button>
                </Div>
              </Card>
            </Div>
          </Group>
        ) : null}

        <Group header={<Header subtitle="Выберите тему или продолжайте уже начатые категории">Категории слов</Header>}>
          <Div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {sortedCategories.map((item) => {
              const difficultyMeta = getDifficultyMeta(item.difficulty);
              const progress = getCategoryProgress(categoryActivity, item);

              return (
                <Card key={item.id} mode="outline" onClick={() => openCategory(item)} className="cursor-pointer">
                  <Div className={`flex h-full flex-col ${progress.hasStarted ? '' : 'justify-center'}`}>
                    <div className={`flex items-start gap-3 ${progress.hasStarted ? 'mb-3' : ''}`}>
                      <CategoryIcon category={item} />
                      <div className="min-w-0 flex-1">
                        <Title level="3" className="break-words">{item.name}</Title>
                        <Text className="mt-1 text-[var(--vkui--color_text_secondary)]">
                          {difficultyMeta.label} · {item.word_count} {pluralize(item.word_count, ['слово', 'слова', 'слов'])}
                          {item.is_premium && !item.is_purchased ? ` · ${item.price_votes || 3} голоса` : ''}
                        </Text>
                      </div>
                      {item.is_premium && !item.is_purchased ? <Icon28LockOutline className="shrink-0 text-[var(--vkui--color_icon_secondary)]" /> : null}
                    </div>
                    {progress.hasStarted ? (
                      <>
                        <div className="mb-1 flex items-center justify-between">
                          <Text className="text-[var(--vkui--color_text_secondary)]">Прогресс</Text>
                          <Text weight="2">{progress.percent}%</Text>
                        </div>
                        <Progress value={progress.percent} />
                        <Text className="mt-2 text-[var(--vkui--color_text_secondary)]">
                          {progress.viewedCount} из {item.word_count} слов открыто
                        </Text>
                      </>
                    ) : null}
                  </Div>
                </Card>
              );
            })}
          </Div>
        </Group>
      </AppShell>
    </Panel>
  );
}
